import { readCatalogCache, writeCatalogCache } from "@/lib/catalogCache";

import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, trackWaClick } from "@/lib/api";
import Navbar from "@/components/Navbar";
import HeroBento from "@/components/HeroBento";
import ProductCard from "@/components/ProductCard";
import CartDrawer from "@/components/CartDrawer";
import { motion } from "framer-motion";
import { Filter, MessageCircle, Clock, TrendingUp } from "lucide-react";
import FloatingCart from "@/components/FloatingCart";
import { useBranch } from "@/context/BranchContext";
import { FASMART_CATS, branchStock, branchPrice } from "@/lib/format";

const BRAND_GROUPS = [
  { id: "fasmart", label: "🛒 FASMART", testid: "category-filter-fasmart", categories: ["EsKrim & FrozenFood", "Sembako", "Minuman", "Snack & Biskuit", "Mie Instan", "Bumbu & Saus", "Kebersihan Rumah", "Perawatan Diri", "Ibu & Bayi", "Perlengkapan Rumah", "Obat & Kesehatan", "Alat Tulis & Kantor"] },
  { id: "bobamie", label: "🧋 BOBA-MIE", testid: "category-filter-bobamie", categories: ["Makanan Siap Saji", "BOBA-MIE"] },
  { id: "fasqua", label: "💧 FASQUA", testid: "category-filter-fasqua", categories: ["FASQUA"] },
  { id: "faslook", label: "👕 FASLOOK", testid: "category-filter-faslook", categories: ["FASLOOK"] },
  { id: "fastrading", label: "📈 FAS TRADING", testid: "category-filter-fastrading", categories: ["FAS TRADING"], link: "/trading" },
  { id: "faskost", label: "🏠 FAS KOST", testid: "category-filter-faskost", categories: ["FAS KOST"], link: "/kost" },
  { id: "faslaundry", label: "🧺 FAS LAUNDRY", testid: "category-filter-faslaundry", categories: ["FAS LAUNDRY"] },
  { id: "fasmusic", label: "🎵 FAS MUSIC", testid: "category-filter-fasmusic", categories: ["FAS MUSIC"], link: "/music" },
  { id: "fasnet", label: "📡 FASNET WiFi", testid: "category-filter-fasnet", categories: ["FASNET WiFi"] },
  { id: "printing", label: "🎨 FAS Printing", testid: "category-filter-printing", categories: ["FAS Printing"] },
  { id: "indotravel", label: "✈️ INDOTRAVEL", testid: "category-filter-indotravel", link: "/indotravel" },
  { id: "rumahrakit", label: "🎣 RUMAH RAKIT", testid: "category-filter-rumahrakit", link: "/rumahrakit" },
];

const DAILY_IDS = ["fasmart"];

const FAS_SERVICES = [
  { label: "FAS Printing", href: "/printing" },
  { label: "FASNET WiFi", href: "/fasnet" },
];

const FASMART_SUBCATEGORIES = ["EsKrim & FrozenFood", "Sembako", "Minuman", "Snack & Biskuit", "Mie Instan", "Bumbu & Saus", "Kebersihan Rumah", "Perawatan Diri", "Ibu & Bayi", "Perlengkapan Rumah", "Obat & Kesehatan", "Alat Tulis & Kantor"];

// Prioritas tampil produk FASMART: Sembako dulu
const FASMART_ORDER = { "EsKrim & FrozenFood": 0, Sembako: 1, Minuman: 2, "Snack & Biskuit": 3, "Mie Instan": 4, "Bumbu & Saus": 5, "Kebersihan Rumah": 6, "Perawatan Diri": 7, "Ibu & Bayi": 8, "Perlengkapan Rumah": 9, "Obat & Kesehatan": 10, "Alat Tulis & Kantor": 11 };

const SUBCATEGORY_MAP = {
  fasmart: FASMART_SUBCATEGORIES.map((s) => ({ id: s, label: s === "EsKrim & FrozenFood" ? "❄️ EsKrim & FrozenFood" : s })),
  bobamie: [
    { id: "Makanan Siap Saji", label: "🍜 Makanan Siap Saji" },
    { id: "BOBA-MIE", label: "🧋 Paket BOBA-MIE" },
  ],
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [prodStatus, setProdStatus] = useState("loading"); // loading | ok | error
  const hasCache = useRef(false);
  const [category, setCategory] = useState("fasmart");
  const [searching, setSearching] = useState(false);
  const [subCategory, setSubCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [onlyPromo, setOnlyPromo] = useState(false);
  const [showFrozenPromo, setShowFrozenPromo] = useState(true);
  const catalogRef = useRef(null);
  const navigate = useNavigate();
  const { branch } = useBranch();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const c = searchParams.get("cat");
    if (c && BRAND_GROUPS.some((g) => g.id === c)) setCategory(c);
  }, [searchParams]);

  const loadProducts = (attempt = 0) => {
    if (attempt === 0 && !hasCache.current) setProdStatus("loading");
    api.get("/products")
      .then((r) => {
        setProducts(r.data);
        setProdStatus("ok");
        writeCatalogCache(r.data);
      })
      .catch(() => {
        if (hasCache.current) { setProdStatus("ok"); return; } // cache tetap tampil bila server gangguan
        if (attempt < 4) setTimeout(() => loadProducts(attempt + 1), 1200 * (attempt + 1));
        else setProdStatus("error");
      });
  };

  useEffect(() => {
    const cached = readCatalogCache();
    if (cached) {
      hasCache.current = true;
      setProducts(cached); // tampil instan dari kunjungan sebelumnya
      setProdStatus("ok");
    }
    loadProducts(); // segarkan diam-diam dari server
    let vid = localStorage.getItem("fas_visitor_id");
    if (!vid) { vid = crypto.randomUUID(); localStorage.setItem("fas_visitor_id", vid); }
    api.post("/track/visit", { visitor_id: vid }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const group = BRAND_GROUPS.find((g) => g.id === category);
    return products.filter((p) => {
      if (!group || !group.categories.includes(p.category)) return false;
      if ((category === "fasmart" || category === "bobamie") && subCategory !== "all" && p.category !== subCategory) return false;
      if (onlyPromo && !p.is_promo) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }).sort((a, b) => (FASMART_ORDER[a.category] ?? 99) - (FASMART_ORDER[b.category] ?? 99))
      .map((p) => {
        if (!FASMART_CATS.includes(p.category)) return p;
        const stok = branchStock(p, branch?.id);
        return { ...p, price: branchPrice(p, branch?.id), stock: stok, out_of_stock: stok <= 0 };
      });
  }, [products, category, subCategory, search, onlyPromo, branch]);

  const scrollToCatalog = () => catalogRef.current?.scrollIntoView({ behavior: "smooth" });
  const goToFrozenPromo = () => { setCategory("fasmart"); setSubCategory("EsKrim & FrozenFood"); setOnlyPromo(false); setSearching(false); setShowFrozenPromo(false); scrollToCatalog(); };

  const wibHour = Number(new Date().toLocaleString("en-US", { timeZone: "Asia/Jakarta", hour: "numeric", hour12: false }));
  const isOpen = wibHour >= 6 && wibHour < 22;

  return (
    <div className="min-h-screen paper-bg">
      <Navbar search={search} setSearch={(v) => { setSearch(v); if (!v.trim()) setSearching(false); }} onSearch={() => { if (search.trim()) { setSearching(true); scrollToCatalog(); api.post("/track/search", { keyword: search.trim() }).catch(() => {}); } }} />
      {!searching && (
      <HeroBento onShopNow={scrollToCatalog} onSeePromo={() => { setOnlyPromo(true); scrollToCatalog(); }} onSeePrinting={() => navigate("/printing")} onSeeSembako={() => { setCategory("fasmart"); setSubCategory("Sembako"); scrollToCatalog(); }} onSeeFasnet={() => navigate("/fasnet")} onSeeFrozen={() => { setCategory("fasmart"); setSubCategory("EsKrim & FrozenFood"); scrollToCatalog(); }} />
      )}

      {/* Referral strip FASNET */}
      {!searching && (
      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-2">
        <button
          data-testid="fasnet-referral-strip"
          onClick={() => navigate("/fasnet")}
          className="w-full rounded-2xl bg-gradient-to-r from-blue-700 via-blue-600 to-orange-500 text-white px-5 py-3.5 flex items-center gap-3 text-left hover:brightness-105 transition"
        >
          <span className="text-2xl">🎁</span>
          <span className="text-sm md:text-base font-semibold">
            Ajak tetangga pasang FASNET WiFi — dapatkan <b>½ Kg Gula Pasir + 5 Bungkus Indomie Kaldu</b>!
          </span>
          <span className="ml-auto shrink-0 text-xs font-mono uppercase tracking-widest bg-white/20 rounded-full px-3 py-1">Info →</span>
        </button>
      </div>
      )}

      <CartDrawer />
      <FloatingCart />

      {/* Popup promo EsKrim & FrozenFood — tampil setiap akses/refresh */}
      {showFrozenPromo && (
        <div data-testid="home-eskrim-popup" className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowFrozenPromo(false)}>
          <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md" onClick={(e) => e.stopPropagation()}>
            <button data-testid="home-eskrim-popup-close" onClick={() => setShowFrozenPromo(false)} aria-label="Tutup promo" className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white shadow-lg text-stone-700 font-black hover:bg-rose-100 hover:text-rose-600 transition flex items-center justify-center">✕</button>
            <img data-testid="home-eskrim-popup-image" src="/promo-eskrim-frozenfood.webp" alt="Promo EsKrim & Frozen Food FASMART — Es Krim mulai Rp 2.000, Frozen Food mulai Rp 10.000" onClick={goToFrozenPromo} className="w-full rounded-3xl shadow-2xl cursor-pointer" />
            <button data-testid="home-eskrim-popup-cta" onClick={goToFrozenPromo} className="mt-3 w-full py-3 rounded-full bg-rose-500 text-white font-black text-sm shadow-xl hover:bg-rose-600 transition">❄️ Belanja EsKrim & Frozen Food →</button>
          </div>
        </div>
      )}

      {/* Running text brand FAS Group — hanya tampil jika >1 brand harian */}
      {DAILY_IDS.length > 1 && (
      <div className="border-y border-[#E5DECF] bg-white/70 overflow-hidden py-2.5" data-testid="brand-marquee">
        <div className="flex w-max animate-marquee gap-3 px-3">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex gap-3 shrink-0" aria-hidden={dup === 1}>
              {BRAND_GROUPS.filter((b) => DAILY_IDS.includes(b.id)).map((b) => (
                <button
                  key={`${dup}-${b.id}`}
                  data-testid={`marquee-brand-${b.id}`}
                  onClick={() => { if (b.link) { navigate(b.link); return; } setCategory(b.id); setSubCategory("all"); scrollToCatalog(); }}
                  className="shrink-0 px-4 py-1.5 rounded-full text-xs font-bold bg-emerald-700/5 text-emerald-900 border border-emerald-700/20 hover:bg-emerald-700 hover:text-white transition whitespace-nowrap"
                >
                  {b.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Category strip */}
      <div ref={catalogRef} className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="flex flex-col items-center gap-3 mb-4">
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight text-stone-900 text-center">
            {onlyPromo ? "Promo Hari Ini" : { fasmart: "Selamat Berbelanja 😊", faslook: "👕 Koleksi FASLOOK", fasqua: "💧 Produk FASQUA", bobamie: "🍜 Menu BOBA-MIE", trading: "📦 Produk FAS TRADING" }[category] || "Selamat Berbelanja 😊"}
          </h2>
          <div className="flex items-center gap-3">
            {category !== "fasmart" && !onlyPromo && (
              <button data-testid="kembali-belanja-btn" onClick={() => { setCategory("fasmart"); setSubCategory("all"); scrollToCatalog(); }} className="shrink-0 px-4 py-2 rounded-full bg-emerald-700 text-white text-xs md:text-sm font-bold hover:bg-emerald-800 transition">← Kembali Belanja</button>
            )}
            {onlyPromo && (
              <button data-testid="clear-promo-filter" onClick={() => setOnlyPromo(false)} className="text-xs font-mono uppercase tracking-widest text-orange-600 hover:text-orange-800">Lihat semua</button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 pb-2">
          {BRAND_GROUPS.filter((c) => DAILY_IDS.includes(c.id) && DAILY_IDS.length > 1).map((c) => (
            <button
              key={c.id}
              data-testid={c.testid}
              onClick={() => { if (c.link) { navigate(c.link); return; } setCategory(c.id); setSubCategory("all"); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                category === c.id
                  ? "bg-stone-900 text-white border-stone-900"
                  : "bg-white text-stone-700 border-[#E5DECF] hover:border-emerald-700"
              }`}
            >
              {c.label}
            </button>
          ))}
          <div className="ml-2 hidden sm:flex items-center gap-1 text-xs text-stone-500 font-mono uppercase tracking-widest">
            <Filter className="w-3 h-3" />
            {filtered.length} produk
          </div>
        </div>
        {SUBCATEGORY_MAP[category] && (
          <div className="mt-3 flex items-center gap-2 overflow-x-auto pb-1" data-testid="fasmart-subcategory-row">
            <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest text-emerald-800 font-semibold">Sub-kategori:</span>
            <button
              data-testid="subcategory-filter-all"
              onClick={() => setSubCategory("all")}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                subCategory === "all" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-[#E5DECF] hover:border-emerald-700"
              }`}
            >
              Semua
            </button>
            {SUBCATEGORY_MAP[category].map((sc) => (
              <button
                key={sc.id}
                data-testid={`subcategory-filter-${sc.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setSubCategory(sc.id)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  subCategory === sc.id ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-[#E5DECF] hover:border-emerald-700"
                }`}
              >
                {sc.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Info jam buka khusus FASMART (tiap brand punya jam operasional berbeda) */}
      {category === "fasmart" && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-4">
          <div data-testid="fasmart-jam-buka-info" className={`rounded-2xl px-4 py-3 md:px-5 md:py-4 flex items-center gap-2 md:gap-3 text-[11px] md:text-base leading-snug shadow-md ${isOpen ? "bg-gradient-to-r from-emerald-700 to-emerald-600 text-emerald-50 shadow-emerald-700/25" : "bg-gradient-to-r from-rose-700 to-rose-600 text-rose-50 shadow-rose-700/25"}`}>
            <Clock className={`w-4 h-4 md:w-6 md:h-6 shrink-0 ${isOpen ? "text-emerald-200" : "text-rose-200"}`} />
            <span>
              <b className="text-white">Jam Buka FASMART:</b> Senin — Minggu · <b className="text-white">06.00 – 22.00</b> · Antar cepat 30–60 menit · <b className="text-white">Gratis ongkir min. belanja Rp 50.000</b>
            </span>
            <span data-testid="fasmart-open-badge" className={`ml-auto shrink-0 px-3 py-1 md:px-4 md:py-1.5 rounded-full text-[9px] md:text-xs font-black uppercase tracking-wide md:tracking-widest text-center leading-tight ${isOpen ? "bg-white text-emerald-700" : "bg-white text-rose-700"}`}>
              {isOpen ? "Toko Buka Nih !" : "Kami Buka Lagi Besok Jam 6 Pagi Ya 😊"}
            </span>
          </div>
        </div>
      )}

      {/* Product grid */}
      {prodStatus === "loading" && (
        <div data-testid="catalog-loading" className="max-w-7xl mx-auto px-4 md:px-8 pb-16 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="rounded-3xl bg-white border border-[#E5DECF] overflow-hidden animate-pulse">
              <div className="aspect-square bg-stone-100" />
              <div className="p-3 space-y-2">
                <div className="h-3 rounded bg-stone-100 w-2/5" />
                <div className="h-4 rounded bg-stone-100 w-full" />
                <div className="h-4 rounded bg-stone-100 w-3/5" />
              </div>
            </div>
          ))}
          <p className="col-span-full text-center text-sm text-stone-400 font-semibold py-2">⏳ Memuat produk…</p>
        </div>
      )}
      {prodStatus === "error" && (
        <div data-testid="catalog-error" className="max-w-7xl mx-auto px-4 md:px-8 pb-16 text-center">
          <div className="inline-block rounded-3xl bg-white border border-[#E5DECF] px-8 py-8">
            <p className="font-display font-bold text-lg text-stone-800">Produk belum termuat</p>
            <p className="text-sm text-stone-500 mt-1">Koneksi ke server terganggu. Coba lagi sebentar.</p>
            <button data-testid="catalog-retry-btn" onClick={() => loadProducts()} className="mt-4 px-6 py-2.5 rounded-full bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800 transition">↻ Muat Ulang Produk</button>
          </div>
        </div>
      )}
      {prodStatus === "ok" && (
      <motion.div
        layout
        className="max-w-7xl mx-auto px-4 md:px-8 pb-16 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-5"
      >
        {filtered.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
        {!filtered.length && (
          <p data-testid="catalog-empty" className="col-span-full text-center text-sm text-stone-400 py-10">Tidak ada produk yang cocok.</p>
        )}
      </motion.div>
      )}

      {/* Footer */}
      <footer className="border-t border-[#E5DECF] bg-white/60">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <div className="inline-block leading-none">
              <div className="font-display font-black text-4xl tracking-tight text-stone-900">FASMART</div>
              <div data-testid="footer-slogan" className="mt-1 flex justify-between w-full text-[10px] font-mono uppercase font-semibold text-emerald-800">
                {"Belanja Dekat, Lebih Hemat.".split("").map((ch, i) => (
                  <span key={i}>{ch === " " ? " " : ch}</span>
                ))}
              </div>
            </div>
            <Link
              to="/loyalty"
              data-testid="footer-member-cta"
              className="mt-3 inline-flex items-center gap-2 rounded-2xl border border-emerald-700/30 bg-emerald-700/5 px-4 py-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-700 hover:text-white hover:shadow-md transition group"
            >
              Dapatkan Diskon &amp; Promo dengan bergabung menjadi member FAS GROUP Corp.
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <div className="font-bold text-stone-900">Admin FAS Group Corp.</div>
                <ul className="mt-2 space-y-2 text-stone-600">
                  <li>
                    <a
                      data-testid="footer-wa-link"
                      onClick={() => trackWaClick("home")}
                      href="https://wa.me/6281377114000?text=Halo%20Admin%20FAS%20Group%2C%20saya%20mau%20tanya..."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800 transition"
                    >
                      <MessageCircle className="w-4 h-4" /> 0813-7711-4000
                    </a>
                  </li>
                </ul>
              </div>
              <img src="https://customer-assets-7cd3h4nn.emergentagent.net/job_fasmart-online/artifacts/kpyu0nvc_ChatGPT%20Image%2026%20Agu%202026%2C%2012.19.43.png" alt="FAS Group Corp" data-testid="footer-fasgroup-logo" className="ml-auto h-14 w-auto object-contain md:h-16 shrink-0" />
            </div>
          </div>
        </div>
        {/* Poin Member — dipindah dari hero ke footer */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-6">
          <div className="rounded-3xl bg-white border border-[#E5DECF] p-6" data-testid="footer-poin-member-card">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 justify-between">
              <div>
                <span className="tag-pill bg-emerald-100 text-emerald-800">
                  <TrendingUp className="w-3 h-3" /> Poin Member
                </span>
                <h3 className="mt-2 font-display font-black text-xl leading-tight text-stone-900">Tiap Rp 1.000 = 1 Poin — tukar jadi voucher & gratis ongkir</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-full bg-amber-200 border-2 border-amber-400 flex items-center justify-center text-xs font-bold">🥉</span>
                <span className="w-9 h-9 rounded-full bg-stone-200 border-2 border-stone-400 flex items-center justify-center text-xs font-bold">🥈</span>
                <span className="w-9 h-9 rounded-full bg-emerald-200 border-2 border-emerald-500 flex items-center justify-center text-xs font-bold">👑</span>
                <span className="text-xs text-stone-500 ml-1">3 tier reward</span>
              </div>
            </div>
          </div>
        </div>
        {/* Layanan FAS Group — dipisah agar fokus belanja harian */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-8">
          <div className="text-xs font-mono uppercase tracking-widest text-stone-500 mb-3">Layanan FAS Group</div>
          <div className="flex flex-wrap gap-2">
            {FAS_SERVICES.map((s) => (
              <button
                key={s.label}
                data-testid={`footer-service-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => navigate(s.href)}
                className="px-4 py-2 rounded-full text-xs font-bold bg-white border border-[#E5DECF] text-stone-700 hover:border-emerald-700 hover:text-emerald-800 transition"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="border-t border-[#E5DECF] py-4 text-center text-xs text-stone-500 font-mono uppercase tracking-widest">
          © 2026 FASMART · Belanja Dekat, Lebih Hemat.
        </div>
      </footer>
    </div>
  );
}
